// App-wide status messages (the old app's "flash" messages).
import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from "react";

type Kind = "ok" | "error";
interface Msg {
  id: number;
  text: string;
  kind: Kind;
}

const FlashContext = createContext<(text: string, kind?: Kind) => void>(() => {});

let nextId = 1;

export function FlashProvider({ children }: { children: ReactNode }) {
  const [msgs, setMsgs] = useState<Msg[]>([]);
  const dismiss = useCallback((id: number) => setMsgs((m) => m.filter((x) => x.id !== id)), []);
  const flash = useCallback(
    (text: string, kind: Kind = "ok") => {
      const id = nextId++;
      setMsgs((m) => [...m, { id, text, kind }]);
      window.setTimeout(() => dismiss(id), kind === "error" ? 12000 : 6000);
    },
    [dismiss],
  );
  const value = useMemo(() => flash, [flash]);
  return (
    <FlashContext.Provider value={value}>
      {msgs.length > 0 && (
        <ul className="flash toasts" role="status">
          {msgs.map((m) => (
            <li key={m.id} className={m.kind}>
              <span>{m.text}</span>
              <button type="button" className="icon-btn" aria-label="Dismiss" onClick={() => dismiss(m.id)}>
                ×
              </button>
            </li>
          ))}
        </ul>
      )}
      {children}
    </FlashContext.Provider>
  );
}

export const useFlash = () => useContext(FlashContext);

/** Show an error from a failed request. */
export function errorText(err: unknown): string {
  return err instanceof Error ? err.message : "Something went wrong.";
}
